import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { liveQuery } from 'dexie'
import { db, ensureSettings } from './lib/db'
import type { AppSettings } from './lib/db'
import { getReadOnly } from './lib/readOnly'
import { useStoreBound } from './lib/useStoreBound'
import { initClock } from './lib/clock'
import { requestPersistentStorage } from './lib/storagePersist'
import { APP_ID } from './lib/healthChecks'
import { applyPersonalization } from './lib/personalization'
import { CUSTOM_THEME_EVENT } from './lib/customThemes'
import { smartTypographyEnabled } from './lib/typographyFlag'
import { deviceNameRef } from './lib/deviceName'
import { AuthGate } from './components/AuthGate'
import { ErrorBoundary } from './components/ErrorBoundary'
import { SyncPill } from './components/SyncPill'
import { SyncNotices } from './components/SyncNotices'
import { UpdatePrompt } from './components/UpdatePrompt'
import { BuildGate } from './components/BuildGate'
import { initSyncMonitor } from './lib/syncMonitor'
import { reportError } from './lib/errorLog'
import './lib/syncLog' // registers the session sync-log sink at startup (feature B)

const DashboardPage       = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.DashboardPage })))
const WritingSurfacePage  = lazy(() => import('./pages/WritingSurface').then(m => ({ default: m.WritingSurfacePage })))
const AO3ExportPage       = lazy(() => import('./pages/AO3Export').then(m => ({ default: m.AO3ExportPage })))
const PlanningWizardPage  = lazy(() => import('./pages/PlanningWizard').then(m => ({ default: m.PlanningWizardPage })))
const SettingsPage        = lazy(() => import('./pages/Settings').then(m => ({ default: m.SettingsPage })))
// Dev-only surface (S3): the styleguide renders every shared primitive for finesse
// preview and regression checks. It exists on the dev server and in the e2e build
// (`npm run build:e2e`, `--mode e2e` into dist-e2e), never in a public deploy. Both
// literals fold at build time, so a production build drops the dynamic import; that is
// proven on the real dist by scripts/build-contract.mjs, which fails the build if the
// page's data-dev-surface marker is present.
const DEV_SURFACES = import.meta.env.DEV || import.meta.env.MODE === 'e2e'
const StyleguidePage      = DEV_SURFACES
  ? lazy(() => import('./pages/Styleguide').then(m => ({ default: m.StyleguidePage })))
  : null

// The tabbed Planning Canvas was retired (P2-4) — the guided wizard is now the single
// planning surface. Redirect any lingering /plan link (menus, bookmarks) to the wizard.
function PlanRedirect() {
  const { workId } = useParams<{ workId: string }>()
  return <Navigate to={`/work/${workId}/wizard`} replace />
}

export function App() {
  // W12 A5: nothing below touches the library until a store is bound (`db` has nothing behind it
  // before that, so a read would throw). A signed-out visitor has none, and sees the landing page.
  const storeBound = useStoreBound()
  useEffect(() => {
    // Always the defaults for a visitor with no library open; the writer's own look follows the store.
    if (!storeBound) { applyPersonalization(undefined); return }
    // A read-only store (newer data, or one that could not be upgraded safely) is never written:
    // no settings seed, no clock offset, no trash purge. It is still read and still shown.
    const writable = getReadOnly() === null
    if (writable) {
      void ensureSettings().catch(() => {})
      void initClock()   // restore the server-anchored clock offset (lib/clock.ts)
    }
    void requestPersistentStorage(APP_ID)   // exempt IndexedDB from eviction (D10)
    // Recovery housekeeping (W12 Phase B): move W11's kept losers into recovery, then drop shelf entries
    // past their 30 days. Dynamic-imported so the recovery tier stays out of the eager boot set
    // (build-contract.json budget.eagerKiB).
    if (writable) void import('./lib/recoveryBoot').then(m => m.runRecoveryBoot()).catch(() => {})
    let latestSettings: AppSettings | undefined
    const sub = liveQuery(() => db.settings.get(1)).subscribe({
      next: s => { latestSettings = s; applyPersonalization(s); smartTypographyEnabled.current = s?.smartTypography ?? false; deviceNameRef.current = s?.deviceName ?? '' },
      error: console.error,
    })
    // Re-apply when the OS light/dark preference flips, so the 'auto' theme follows
    // the system live (no-op for the explicit themes).
    const mql = window.matchMedia?.('(prefers-color-scheme: dark)')
    const onScheme = () => applyPersonalization(latestSettings)
    mql?.addEventListener('change', onScheme)
    // A pasted custom theme is per device (localStorage, not Dexie), so no liveQuery sees it change:
    // Settings fires this event instead (lib/customThemes.ts).
    window.addEventListener(CUSTOM_THEME_EVENT, onScheme)
    const teardownMonitor = initSyncMonitor()
    return () => { sub.unsubscribe(); mql?.removeEventListener('change', onScheme); window.removeEventListener(CUSTOM_THEME_EVENT, onScheme); teardownMonitor() }
  }, [storeBound])

  return (
    <>
      {/* Skip link — first focusable element in the shell, always rendered
          regardless of AuthGate's state (splash / sign-in / cold-start / failed /
          ready). Its target (#main) is kept alive in every one of those states so
          the link never points at nothing — see AuthGate/index.tsx and
          AuthGate/LoadingGate.tsx. */}
      <a className="skip-link" href="#main">Skip to content</a>
      {/* The update layer sits ABOVE every gate (app-platform W14, B4): it registers the service
          worker and offers new builds for signed-out visitors too, so the landing page works
          offline and a long-open sign-in screen still learns about a deploy. */}
      <UpdatePrompt />
      {/* The stale-build gate wraps AuthGate, so an old build takes the deployed one before it
          signs in or touches OneDrive. Fails open everywhere. */}
      <BuildGate>
      <AuthGate>
        <BrowserRouter>
          {/* Fixed app-wide overlays, inside the Router so they can navigate (the
              SyncPill links to the Settings sync-activity view). */}
          <SyncPill />
          <SyncNotices />
          <div id="main" tabIndex={-1}>
            <ErrorBoundary onError={(error) => reportError(error, 'render')}>
            <Suspense fallback={<div style={{ minHeight: '60dvh' }} />}>
              <Routes>
                <Route path="/"                               element={<DashboardPage />} />
                <Route path="/work/:workId"                   element={<DashboardPage />} />
                <Route path="/work/:workId/write/:chapterId"  element={<WritingSurfacePage />} />
                <Route path="/work/:workId/plan"              element={<PlanRedirect />} />
                <Route path="/work/:workId/export"            element={<AO3ExportPage />} />
                <Route path="/work/:workId/wizard"            element={<PlanningWizardPage />} />
                <Route path="/settings"                       element={<SettingsPage />} />
                {StyleguidePage && <Route path="/styleguide"  element={<StyleguidePage />} />}
                <Route path="*"                               element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            </ErrorBoundary>
          </div>
        </BrowserRouter>
      </AuthGate>
      </BuildGate>
    </>
  )
}
